import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import BrandMark from '../brand/BrandMark';
import { useColorMode } from '../../theme/ThemeProvider';
import './landing.css';

interface LandingPageProps {
  onOpenFile: () => void;
  onViewSample: () => void;
}

export default function LandingPage({ onOpenFile, onViewSample }: LandingPageProps) {
  const { resolvedMode, setMode } = useColorMode();

  return (
    <div className="landing-page">
      <a className="skip-link" href="#main-content">Skip to content</a>

      <header className="landing-header">
        <div className="landing-shell landing-header__inner">
          <a className="landing-brand" href="/" aria-label="MarkGlass home">
            <span className="landing-brand__mark"><BrandMark size={25} /></span>
            <span>MarkGlass</span>
          </a>

          <nav className="landing-header__nav" aria-label="Main navigation">
            <a href="#details">Why MarkGlass</a>
            <button type="button" onClick={onViewSample}>View sample</button>
            <button
              className="landing-theme"
              type="button"
              onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
              aria-label={`Switch to ${resolvedMode === 'dark' ? 'light' : 'dark'} theme`}
              title={`Switch to ${resolvedMode === 'dark' ? 'light' : 'dark'} theme`}
            >
              {resolvedMode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </button>
          </nav>
        </div>
      </header>

      <main id="main-content" className="landing-main">
        <section className="landing-hero landing-shell" aria-labelledby="landing-title">
          <div className="landing-hero__copy">
            <p className="landing-eyebrow"><span className="landing-eyebrow__line" /> A dedicated Markdown reader</p>
            <h1 id="landing-title">Markdown,<br /><em>ready to read.</em></h1>
            <p className="landing-hero__description">
              Open a README, spec, or agent instruction file and see the whole document. MarkGlass gives code, diagrams, tables, and math room to make sense.
            </p>
            <div className="landing-hero__actions">
              <button className="landing-button landing-button--primary" type="button" onClick={onOpenFile}>
                <UploadFileOutlinedIcon fontSize="small" />
                Open a Markdown file
                <ArrowForwardRoundedIcon className="landing-button__arrow" fontSize="small" />
              </button>
              <button className="landing-text-button" type="button" onClick={onViewSample}>
                Explore a sample <ArrowForwardRoundedIcon fontSize="small" />
              </button>
            </div>
            <p className="landing-privacy"><span aria-hidden="true" /> Files opened from your device stay in your browser.</p>
          </div>

          <div className="landing-hero__visual">
            <div className="landing-preview" aria-label="Preview of a rendered Markdown document">
              <div className="landing-preview__bar">
                <span className="landing-preview__file"><BrandMark size={16} /> edge-cache.md</span>
                <span className="landing-preview__mode">READING VIEW</span>
              </div>
              <div className="landing-preview__content">
                <aside className="landing-preview__toc" aria-hidden="true">
                  <span>CONTENTS</span>
                  <b>Overview</b>
                  <span>Request flow</span>
                  <span>Response policy</span>
                  <span>Implementation</span>
                </aside>
                <div className="landing-preview__article">
                  <span className="landing-preview__kicker">ARCHITECTURE NOTE</span>
                  <h2>Edge cache</h2>
                  <p>An edge cache serves repeated reads quickly while keeping the path back to the source of truth explicit.</p>
                  <div className="landing-preview__rule" />
                  <h3>Request flow</h3>
                  <div className="landing-flow" aria-label="Client to edge cache to origin">
                    <span>Client</span><i aria-hidden="true" /><span className="landing-flow__active">Edge cache</span><i aria-hidden="true" /><span>Origin</span>
                  </div>
                  <h3>Response policy</h3>
                  <div className="landing-preview__table" aria-hidden="true">
                    <div><strong>Response</strong><strong>Lifetime</strong></div>
                    <div><span>Public catalog</span><span>5 minutes</span></div>
                    <div><span>Account data</span><span>None</span></div>
                  </div>
                </div>
              </div>
            </div>
            <p className="landing-preview__caption"><span>01 / 03</span> A document, with space to think.</p>
          </div>
        </section>

        <section id="details" className="landing-details" aria-labelledby="details-title">
          <div className="landing-shell landing-details__inner">
            <div className="landing-details__intro">
              <p className="landing-eyebrow"><span className="landing-eyebrow__line" /> Made for real documents</p>
              <h2 id="details-title">The details are<br /><em>the document.</em></h2>
              <p>Formatting carries meaning in technical writing. MarkGlass keeps long, complex Markdown legible without putting an editor beside it.</p>
            </div>
            <div className="landing-features">
              <div className="landing-feature">
                <span className="landing-feature__number">01</span>
                <div><h3>Find your place</h3><p>Move through a document with a live table of contents, heading links, and in-document search.</p></div>
              </div>
              <div className="landing-feature">
                <span className="landing-feature__number">02</span>
                <div><h3>Keep the detail</h3><p>Read syntax-highlighted code, Mermaid diagrams, equations, tables, footnotes, and callouts as they were intended.</p></div>
              </div>
              <div className="landing-feature">
                <span className="landing-feature__number">03</span>
                <div><h3>Make it yours</h3><p>Adjust the reading width and theme. Copy, download, or print the document when you need to take it further.</p></div>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-close" aria-labelledby="close-title">
          <div className="landing-shell landing-close__inner">
            <div>
              <p className="landing-close__eyebrow">START READING</p>
              <h2 id="close-title">Give your next file a clearer view.</h2>
            </div>
            <button className="landing-button landing-button--light" type="button" onClick={onOpenFile}>
              <UploadFileOutlinedIcon fontSize="small" /> Open a Markdown file <ArrowForwardRoundedIcon className="landing-button__arrow" fontSize="small" />
            </button>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-shell landing-footer__inner">
          <span><BrandMark size={17} /> MarkGlass <small>by DevDigest</small></span>
          <div><a href="mailto:devdigest.net@gmail.com">Support</a><span>© {new Date().getFullYear()} DevDigest LLC</span></div>
        </div>
      </footer>
    </div>
  );
}
